import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TaskConstants } from "../../../constants";

import {
  removeTask,
  setActiveTask,
  setTaskAttributeValue,
  setTaskPreventAnimation,
  setTaskTargeted as setTaskTargetedAction,
  updateTask as updateTaskAction,
} from "../../../redux/actions/tasks";
import { ReactComponent as AlertIcon } from "../../../assets/icons/alert_black_24dp.svg";
import { ReactComponent as TargetIcon } from "../../../assets/icons/crosshair.svg";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import Button from "../../input/button";
import AttributeIndicator from "./AttributeIndicator";
import { setOverlayVisible } from "../../../redux/actions/overlay";
import { Box, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import useTaskDeadline from "../../../hooks/tasks/useTaskDeadline";
import NotesContainer from "../taskInputs/notes/NotesContainer";
import { Emoji } from "emoji-picker-react";
import useWindowDimensions from "../../../helpers/window";
import removeNotificationsForTask from "../../../notifications/removeNotificationsForTask";
import { Capacitor } from "@capacitor/core";
import { pluralise } from "../../../helpers/text";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import useTaskAPIClient from "../../../api/task/hooks/useTaskAPIClient";
import LabelDisplay from "./LabelDisplay";

dayjs.extend(advancedFormat);
dayjs.locale("en"); // Set the locale

const intervalShorthand = {
  day: "D",
  week: "W",
  month: "M",
  year: "Y",
};

const Task = ({ item, forceListToResort, compact = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { width } = useWindowDimensions();
  const {
    completeTask,
    cancelTask,
    updateTask,
    setTaskAttribute,
    setTaskTargeted,
    setNoteCompletionStatus,
    removeNote,
  } = useTaskAPIClient();

  const {
    taskId,
    name,
    notes: initialNotes,
    attributes,
    labels,
    targeted,
    preventAnimation,
    deadline,
    created,
    frequency,
    emoji,
    schedule,
  } = item;
  const { difficulty, importance } = attributes;

  const [animation, setAnimation] = useState("animate__fadeIn");
  const [isComplete, setIsComplete] = useState(false);
  const [notes, setNotes] = useState(initialNotes);
  const [isTargeting, setIsTargeting] = useState(false);

  const { deadlineDisplay, closeToDeadline } = useTaskDeadline(
    deadline,
    created,
    forceListToResort,
  );

  const recurringLabelText = useMemo(() => {
    if (frequency) {
      const { intervalCount, intervalDateType } = frequency;
      if (compact) {
        const short =
          intervalShorthand[intervalDateType] ||
          intervalDateType[0].toUpperCase();
        return `Every ${intervalCount > 1 ? `${intervalCount}` : "1"}${short}`;
      }
      return `Every${intervalCount > 1 ? ` ${intervalCount}` : ""} ${pluralise(intervalDateType, intervalCount)}`;
    }
    return "Does not repeat";
  }, [frequency, compact]);

  const scheduleIsInPast = useMemo(() => {
    return schedule && dayjs.unix(schedule).isBefore(dayjs());
  }, [schedule]);

  const scheduleText = useMemo(() => {
    if (!schedule) {
      return "No schedule set";
    }
    if (compact) {
      const sameYear = dayjs.unix(schedule).isSame(dayjs(), "year");
      const dateStr = sameYear
        ? dayjs.unix(schedule).format("ddd D MMM")
        : dayjs.unix(schedule).format("ddd D MMM YY");
      return `${dayjs.unix(schedule).format("h:mmA")} ${dateStr}`;
    }
    const dateFormat = dayjs.unix(schedule).isSame(dayjs(), "year")
      ? "dddd Do MMMM"
      : "dddd Do MMMM YYYY";
    return `${dayjs.unix(schedule).format("h:mmA")} on ${dayjs.unix(schedule).format(dateFormat)}`;
  }, [schedule, scheduleIsInPast, compact]);

  const onCompleteClick = () => {
    const onConfirmCallback = () => {
      if (isComplete) {
        return;
      }
      setIsComplete(true);

      //Wait 500ms for the completion animation to finish
      setTimeout(() => {
        // Then animate the task off screen
        dispatch(setTaskPreventAnimation({ taskId, value: false }));
        setAnimation("animate__backOutRight animate__faster");

        setTimeout(async () => {
          //Finally remove the task and notify server
          dispatch(removeTask(taskId));
          await completeTask(taskId);

          if (Capacitor.isNativePlatform()) {
            await removeNotificationsForTask(taskId);
          }
        }, 400);
        dispatch(setOverlayVisible({ visible: false }));
      }, 500);
    };

    dispatch(
      setOverlayVisible({
        visible: true,
        overlayType: "confirm",
        confirmationText: "Have you completed this task?",
        confirmCallback: onConfirmCallback,
      }),
    );
  };

  const onTargetClick = async () => {
    setIsTargeting(true);
    try {
      await setTaskTargeted(taskId, !targeted);
      dispatch(setTaskTargetedAction({ taskId, targeted: !targeted }));
    } finally {
      setIsTargeting(false);
    }
  };

  const onCancelClick = () => {
    const onConfirmCallback = () => {
      dispatch(setTaskPreventAnimation({ taskId, value: false }));
      setAnimation("animate__backOutRight animate__faster");
      setTimeout(async () => {
        dispatch(removeTask(taskId));
        await cancelTask(taskId);
        await removeNotificationsForTask(taskId);
      }, 400);
      dispatch(setOverlayVisible({ visible: false }));
    };

    dispatch(
      setOverlayVisible({
        visible: true,
        overlayType: "confirm",
        confirmationText: "Are you sure you want to cancel this task?",
        confirmCallback: onConfirmCallback,
      }),
    );
  };

  const onEditClick = () => {
    dispatch(setActiveTask(taskId));
    const fromPage = location.pathname.includes("/planner")
      ? "planner"
      : location.pathname.includes("/dashboard")
        ? "dashboard"
        : "tasks";
    navigate(`/edit-task?from=${fromPage}`);
  };

  const onAttributeChange = (attributeType) => {
    return async (value) => {
      dispatch(setTaskAttributeValue({ taskId, attributeType, value }));
      await setTaskAttribute(taskId, attributeType, value);
    };
  };

  const onNoteStatusChange = async ({ id, status }) => {
    const newNotes = { ...notes };
    const newNote = newNotes[id];
    newNote.completed = status;

    setNotes(newNotes);

    await setNoteCompletionStatus(taskId, id, status);
  };

  const onNoteDeleted = async (id) => {
    const newNotes = { ...notes };
    delete newNotes[id];

    setNotes(newNotes);

    await removeNote(taskId, id);
  };

  const onLabelsChange = async (newLabels) => {
    // Update Redux store
    dispatch(updateTaskAction(taskId, { ...item, labels: newLabels }));

    // Update backend
    const labelsPatch = {
      op: "replace",
      path: "/labels",
      value: newLabels,
    };
    await updateTask({ taskId, patches: [labelsPatch] });
  };

  const onLabelRemoved = (labelId) => {
    const newLabels = labels.filter((id) => id !== labelId);
    onLabelsChange(newLabels);
  };

  return (
    <div
      className={`animate__animated ${animation && !preventAnimation ? animation : ""} task ${targeted ? "task--targeted" : ""} ${closeToDeadline ? "task--close-to-deadline" : ""}${emoji ? " task--has-emoji" : ""}`}
    >
      <div className={`task__header`}>
        <div className={`task__header-contents`}>
          <div className="task__header-emoji-border-mask-right" />
          <div className="task__header-emoji-border-mask-bottom" />
          {emoji && (
            <div className="task_emoji-badge">
              <div className="task_emoji-background" />
              <div className="task_emoji-container">
                <Emoji unified={emoji} size={width > 768 ? 30 : 24} />
              </div>
            </div>
          )}
          {targeted && <TargetIcon className="task__header-target-icon" />}
          <Typography variant="h4NoMargin" className="task__title">
            {name}
          </Typography>
          <LabelDisplay
            labels={labels}
            taskId={taskId}
            onLabelsChange={onLabelsChange}
            onLabelRemoved={onLabelRemoved}
          />
        </div>
      </div>
      {schedule && (
        <div
          className={`task__schedule-container ${scheduleIsInPast ? "task__schedule-container--past" : ""}`}
        >
          <AlertIcon className={`task__schedule-icon`} />
          {scheduleIsInPast ? (
            <Typography variant="body1" className="task__schedule-text">
              Was scheduled for: {scheduleText}
            </Typography>
          ) : (
            <Typography variant="body1" className="task__schedule-text">
              Scheduled for: {scheduleText}
            </Typography>
          )}
        </div>
      )}
      <div className="task__body">
        <div className="task__attributes">
          <div className="task__attribute">
            <Typography variant="body1" className="task__attribute-title">
              Difficulty
            </Typography>
            <AttributeIndicator
              value={difficulty}
              onAttributeChange={onAttributeChange(
                TaskConstants.ATTRIBUTE_DIFFICULTY,
              )}
            />
          </div>
          <div className="task__attribute">
            <Typography variant="body1" className="task__attribute-title">
              Importance
            </Typography>
            <AttributeIndicator
              value={importance}
              onAttributeChange={onAttributeChange(
                TaskConstants.ATTRIBUTE_IMPORTANCE,
              )}
            />
          </div>
        </div>
        <div className="task__right-column">
          <div className="task__notes-container">
            {notes && (
              <NotesContainer
                notes={notes}
                showInput={false}
                onNoteStatusChange={onNoteStatusChange}
                onNoteDeleted={onNoteDeleted}
              />
            )}
          </div>
        </div>
      </div>
      <div className={`task__action-bar`}>
        {deadlineDisplay && (
          <Box className="task__action-deadline-container">
            <AlertIcon className={`task__action-deadline-icon`} />
            <Typography
              variant="body1"
              className={`task__action-deadline-time`}
            >
              {deadlineDisplay}
            </Typography>
          </Box>
        )}
        {frequency && (
          <Box className="task__action-recurring-container">
            <EventRepeatIcon className={`task__action-recurring-icon`} />
            <Typography
              variant="body1"
              className={`task__action-recurring-info`}
            >
              {recurringLabelText}
            </Typography>
          </Box>
        )}
        <Box className="task__action-button-container">
          <Button
            text="Cancel"
            color="red"
            icon="trash"
            collapsable={true}
            onClick={onCancelClick}
          />
          <Button
            text="Edit"
            color="orange"
            icon="edit"
            collapsable={true}
            onClick={onEditClick}
          />
          <Button
            text={targeted ? "Detarget" : "Target"}
            color="yellow"
            icon={targeted ? "detarget" : "target"}
            collapsable={true}
            loading={isTargeting}
            onClick={onTargetClick}
          />
          <Button
            text="Complete"
            color="green"
            icon="tick"
            collapsable={true}
            onClick={onCompleteClick}
          />
        </Box>
      </div>
    </div>
  );
};

export default Task;
